import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Button, Surface } from '@americanexpress/dls-react';
import { IconClose } from '@americanexpress/dls-icons';
import { useDateSelector } from '../../context/dateContext';
import { setEndDate, setStartDate } from '../../ducks/dateDuck';
import {
  getEndDate,
  getIsDateRange,
  getMinShownDateString,
  getStartDate,
} from '../../selectors/dateSelectors';
import {
  getEndTime,
  getHasTimeDropdown,
  getStartTime,
  getNewLabelEnabled,
} from '../../selectors/timeSelectors';
import DateInput from '../DateInput/DateInput';
import CalendarFooter from '../CalendarFooter/CalendarFooter';
import styles from './PopupMobile.scss';
import DaysOfTheWeek from '../DaysOfTheWeek/DaysOfTheWeek';
import CalendarVertical from '../CalendarVertical/CalendarVertical';
import { useTimeSelector } from '../../context/timeContext';
import TimeDropdown from '../TimeDropdown/TimeDropdown';
import { setEndTime, setStartTime } from '../../ducks/timeDuck';
import { convertDefaultFormatStringDateToLocaleFormat } from '../../utils/dateUtils';
import { getTimeLabels } from '../../utils/timeUtils';

const PopupMobile = ({ onClosePopup, placeholder }) => {
  const popupRef = useRef(null);

  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const startDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(startDate);
  const endDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(endDate);
  const isDateRange = useDateSelector(getIsDateRange);
  const minShownDate = useDateSelector(getMinShownDateString);
  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);

  const hasTimeDropdown = useTimeSelector(getHasTimeDropdown);
  const isEnabledNewTimeLabel = useTimeSelector(getNewLabelEnabled);
  const timeLabels = getTimeLabels(isEnabledNewTimeLabel);

  const { formatMessage } = useIntl();

  useEffect(() => {
    const htmlHead = document.querySelector('html');
    htmlHead.style.position = 'fixed';

    if (popupRef.current?.parentElement && typeof popupRef.current.parentElement.scroll === 'function') {
      popupRef.current.parentElement.scroll({ top: 0 });
    }

    return () => {
      htmlHead.style.position = '';
    };
  }, []);

  return (
    <Surface
      variant="foreground"
      className={clsx(
        styles.popupMobile,
        'flex-column flex-justify-between'
      )}
      ref={popupRef}
    >
      {startDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage(
            { id: 'aria.start_date_update' },
            { startDateLocalFormat }
          )}
          {startTime && hasTimeDropdown && (
            <span id="startTime"> {startTime} </span>
          )}
        </div>
      )}
      {endDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage(
            { id: 'aria.end_date_update' },
            { endDateLocalFormat }
          )}
          {endTime && hasTimeDropdown && <span id="endTime"> {endTime} </span>}
        </div>
      )}
      <Surface
        variant="foreground"
        className={clsx(styles.header, 'flex pad-1 flex-column flex-justify-end')}
        role="dialog"
        aria-label={formatMessage({ id: 'aria.popup.chooseDate' })}
        id="date_popup"
      >
        <div className="flex flex-justify-end flex-align-items-center">
          <Button
            icon={<IconClose size="md" />}
            onClick={onClosePopup}
            aria-label="Close"
            variant="tertiary"
            className="pad-1"
          />
        </div>
        <div className="flex row margin-2-b">
          <div
            className={clsx(
              hasTimeDropdown || !isDateRange
                ? 'col-sm-12 margin-1-b'
                : 'col-sm-6 pad-0-r'
            )}
          >
            <div className="flex row">
              <div
                className={clsx(
                  hasTimeDropdown ? 'col-xs-6' : 'col-sm-12 margin-1-b'
                )}
              >
                <DateInput
                  className={clsx(styles.input)}
                  onChangeAction={setStartDate}
                  stateKey="startDate"
                  initialDate={minShownDate}
                  placeholder={placeholder.startDate}
                />
              </div>
              {hasTimeDropdown && (
                <div className="col-xs-6">
                  <TimeDropdown
                    ariaLabel={formatMessage({
                      id: timeLabels.timeAriaLabelForDeparture,
                    })}
                    stateKey="startTime"
                    onChangeAction={setStartTime}
                  />
                </div>
              )}
            </div>
          </div>
          {isDateRange && (
            <div
              className={clsx(
                hasTimeDropdown ? 'col-sm-12' : 'col-sm-6 pad-0-r pad-2-b'
              )}
            >
              <div
                className={clsx('row flex', {
                  'margin-1-t': hasTimeDropdown,
                })}
              >
                <div
                  className={clsx(
                    hasTimeDropdown ? 'col-xs-6' : 'col-sm-12'
                  )}
                >
                  <DateInput
                    className={clsx(styles.input)}
                    onChangeAction={setEndDate}
                    stateKey="endDate"
                    initialDate={startDate}
                    placeholder={placeholder.endDate}
                  />
                </div>
                {hasTimeDropdown && (
                  <div className="col-xs-6">
                    <TimeDropdown
                      ariaLabel={formatMessage({
                        id: timeLabels.timeAriaLabelForArrival,
                      })}
                      stateKey="endTime"
                      onChangeAction={setEndTime}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DaysOfTheWeek />
      </Surface>
      <CalendarVertical tabIndex="0" />
      <CalendarFooter className={styles.footer} onClose={onClosePopup} />
    </Surface>
  );
};

PopupMobile.propTypes = {
  placeholder: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }),
  onClosePopup: PropTypes.func.isRequired,
};

PopupMobile.defaultProps = {
  placeholder: {},
};

export default memo(PopupMobile);
